import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { InputText } from "primereact/inputtext";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";

import UiTable from "../../../components/table/Table.component";
import UiButton from "../../../components/button/Button";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import {
	TableColumn,
	TableColumnTemplate,
} from "../../../models/components/table/table-column.model";
import { TableMessages } from "../../../models/components/table/table-message.model";
import {
	FieldMessageSeverity,
	UiFieldMessageProps,
} from "../../../models/components/field-message.model";
import { TablePagination } from "../../../models/components/table/table-pagination.model";

import { PageProps } from "../../../models/routing.model";
import { MarketingCampaignRow } from "../../../models/pages/settings/marketing-campaign-row.model";
import { useMarketingCampaignsAdmin } from "../../../hooks/useMarketingCampaignsAdmin";

import "./marketing-campaigns.css";

const LIST_ROUTE = "/settings/marketing-campaigns";

const SettingsMarketingCampaigns = (props: PageProps) => {
	const { t } = useTranslation("common");
	const navigate = useNavigate();

	const campaigns = useMarketingCampaignsAdmin();

	const [filterName, setFilterName] = useState("");
	// Delete just deletes; only a failure surfaces a message, shown inline (like
	// ParticipantsCreateModal) rather than as a toast.
	const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

	const goToEdit = (campaignId: number) => navigate(`${LIST_ROUTE}/${campaignId}`);
	const goToNew = () => navigate(`${LIST_ROUTE}/new`);

	const handleDelete = (id: number, name: string) =>
		confirmDialog({
			message: t("MARKETING_CAMPAIGNS.CONFIRM.DELETE_MESSAGE", { name }),
			header: t("MARKETING_CAMPAIGNS.CONFIRM.DELETE_HEADER"),
			icon: "pi pi-trash",
			acceptClassName: "p-button-danger",
			acceptLabel: t("MARKETING_CAMPAIGNS.CONFIRM.DELETE_ACCEPT"),
			rejectLabel: t("MARKETING_CAMPAIGNS.CONFIRM.CANCEL"),
			accept: async () => {
				setDeleteMessage(null);
				try {
					await EndpointsService.dataRetriever.deleteMarketingCampaign({ id });
					await campaigns.invalidate();
				} catch {
					setDeleteMessage("MARKETING_CAMPAIGNS.TOAST.DELETE_ERROR");
				}
			},
		});

	const rows = useMemo<MarketingCampaignRow[]>(() => {
		const term = filterName.trim().toLowerCase();
		return (campaigns.data ?? [])
			.filter((campaign) => !term || campaign.name.toLowerCase().includes(term))
			.map(
				(campaign) =>
					new MarketingCampaignRow({ campaign, onEdit: goToEdit, onDelete: handleDelete })
			);
	}, [campaigns.data, filterName]);

	const columns: TableColumn[] = [
		{
			field: "name",
			title: "MARKETING_CAMPAIGNS.LIST.COLUMNS.NAME",
			sortable: true,
			style: { width: "45%" },
		},
		{
			field: "schedule",
			title: "MARKETING_CAMPAIGNS.LIST.COLUMNS.SCHEDULE",
			sortable: true,
			style: { width: "30%" },
		},
		{
			field: "messagesCount",
			title: "MARKETING_CAMPAIGNS.LIST.COLUMNS.MESSAGES",
			sortable: true,
			alignment: "center",
			style: { width: "15%" },
		},
		{
			field: "actionsTemplate",
			sortable: false,
			template: TableColumnTemplate.BUTTONS,
			alignment: "center",
			style: { width: "10%" },
		},
	];

	const message: UiFieldMessageProps | undefined = useMemo(() => {
		const messages = new TableMessages();
		if (campaigns.isLoading) {
			return { severity: FieldMessageSeverity.LOADING, label: messages.loading };
		}
		if (campaigns.isError) {
			return { severity: FieldMessageSeverity.DANGER, label: messages.error };
		}
		if (rows.length === 0) {
			return { severity: FieldMessageSeverity.INFO, label: messages.empty };
		}
		return undefined;
	}, [campaigns.isLoading, campaigns.isError, rows.length]);

	const [pagination, setPagination] = useState<TablePagination>({
		first: 0,
		rows: 10,
		page: 0,
		sortField: "name",
		sortOrder: 1,
		search: undefined,
		filters: undefined,
	});

	return (
		<div
			className="mc-page"
			id={props.id}
		>
			<ConfirmDialog />
			{deleteMessage && (
				<div className="form-message">
					<UiFieldMessage
						severity={FieldMessageSeverity.DANGER}
						label={deleteMessage}
					/>
				</div>
			)}
			<UiTable
				useAsCard
				dataKey="id"
				title="MARKETING_CAMPAIGNS.LIST.TITLE"
				customHeader={
					<UiButton
						type="button"
						className="p-button-rounded p-button-sm"
						icon="pi pi-plus"
						label="MARKETING_CAMPAIGNS.LIST.NEW"
						onClick={goToNew}
					/>
				}
				secondaryHeader={
					<div className="mc-list-filters">
						<span className="p-input-icon-left mc-list-filter">
							<i className="pi pi-search" />
							<InputText
								value={filterName}
								onChange={(event) => setFilterName(event.target.value)}
								placeholder={t("MARKETING_CAMPAIGNS.LIST.FILTER_NAME")}
							/>
						</span>
					</div>
				}
				columns={columns}
				value={rows}
				message={message}
				pagination={pagination}
				paginationFn={(event) => {
					setPagination({ ...pagination, ...event });
				}}
			/>
		</div>
	);
};

export default SettingsMarketingCampaigns;
